import { Link } from "@tanstack/react-router"
import { Badge } from "design-system/atoms/Badge"
import { Card } from "design-system/molecules/Card"
import DateDisplay from "design-system/molecules/DateDisplay"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { ArrowUpRight } from "lucide-react"
import { ChannelIcon } from "modules/shared/components/Miscelanea/ChannelIcon"
import { useTranslation } from "react-i18next"
import {
  CapabilityExecutionsResponseDataSchema,
  CapabilityResponseDataSchema,
  ConversationTypeEnum,
  resolveCapabilityReasonLabel,
} from "schema"
import z from "zod"

type ExecutionItemProps = {
  capability: z.infer<typeof CapabilityResponseDataSchema>
  execution: z.infer<typeof CapabilityExecutionsResponseDataSchema>[number]
}

function customerLabel(
  customer: {
    name: string | null
    email: string | null
    phone: string | null
  } | null,
) {
  if (!customer) return "—"
  return customer.name || customer.email || customer.phone || "—"
}

export function ExecutionItem({ execution, capability }: ExecutionItemProps) {
  const { t } = useTranslation()

  const status = execution.status
  const isSuccess = status === "Success"
  const isUnsuccess = status === "Unsuccess"
  const isCached = status === "Cached"
  const isError = status === "Error"
  const isSkipped = status === "Skipped"
  const hasResult = !!execution.result && typeof execution.result === "object"
  const isEmptyResult =
    hasResult &&
    (Array.isArray(execution.result)
      ? execution.result.length === 0
      : Object.keys(execution.result).length === 0)
  const skipOrErrorReason =
    isUnsuccess || isError || isSkipped
      ? resolveCapabilityReasonLabel(capability.identifier, execution.reason)
      : null
  const conversation = execution.conversation
  const customerId = conversation?.customerId
  const isPlayground = conversation?.type === ConversationTypeEnum.Testing
  const campaignName = conversation?.campaign?.name?.trim()
    ? conversation.campaign.name
    : t("campaigns.untitled")

  const resultJson =
    execution.result && !(isError || isSkipped) ? JSON.stringify(execution.result, null, 2) : null

  return (
    <Card className="p-4">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            {isSuccess && <Badge variant="success">{t("capabilities.executions.success")}</Badge>}
            {isUnsuccess && (
              <Badge variant="unsuccess">{t("capabilities.executions.unsuccess")}</Badge>
            )}
            {isCached && <Badge variant="cached">{t("capabilities.executions.cached")}</Badge>}
            {isError && <Badge variant="negative">{t("capabilities.executions.error")}</Badge>}
            {isSkipped && <Badge variant="warning">{t("capabilities.executions.skipped")}</Badge>}
            {isPlayground && (
              <Badge variant="testing">
                <span>{t("campaigns.detail.playground")}</span>
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <DateDisplay date={execution.createdAt} format="distance" />
          </div>
        </div>
        {conversation && (
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <span className="font-medium">{customerLabel(conversation.customer)}</span>
            <span className="text-muted-foreground">{" · "}</span>
            <span className="text-muted-foreground">{campaignName}</span>
            <span className="text-muted-foreground">{" · "}</span>
            <ChannelIcon channel={conversation.channel} withTooltip />
          </div>
        )}
        {!isSkipped && execution.provider && (
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">{t("capabilities.executions.provider")}</span>
            <span className="text-muted-foreground">{execution.provider}</span>
          </div>
        )}
        {isError && skipOrErrorReason && (
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-destructive">
              {t("capabilities.executions.errorMessage")}
            </p>
            <MarkdownRenderer codeClassName="overflow-x-auto whitespace-pre-wrap break-words">
              {`\`\`\`json\n${skipOrErrorReason}\n\`\`\``}
            </MarkdownRenderer>
          </div>
        )}
        {isUnsuccess && skipOrErrorReason && (
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-testing-1">
              {t("capabilities.executions.unsuccessReason")}
            </p>
            <MarkdownRenderer codeClassName="overflow-x-auto whitespace-pre-wrap break-words">
              {`\`\`\`json\n${skipOrErrorReason}\n\`\`\``}
            </MarkdownRenderer>
          </div>
        )}
        {isSkipped && skipOrErrorReason && (
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-warning">
              {t("capabilities.executions.skipReason")}
            </p>
            <MarkdownRenderer codeClassName="overflow-x-auto whitespace-pre-wrap break-words">
              {`\`\`\`json\n${skipOrErrorReason}\n\`\`\``}
            </MarkdownRenderer>
          </div>
        )}
        {hasResult && !isEmptyResult && resultJson && capability.kind !== "Native" && (
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">{t("capabilities.executions.result")}</p>
            <MarkdownRenderer collapseJson>{`\`\`\`json\n${resultJson}\n\`\`\``}</MarkdownRenderer>
          </div>
        )}
        {execution.conversationId && customerId && (
          <div className="flex items-center gap-2 text-sm">
            <Link
              to="/conversations/list/$customerId"
              params={{ customerId }}
              search={{ conversationId: Number(execution.conversationId), tab: "conversations" }}
              className="flex items-center gap-1 text-primary underline underline-offset-2 hover:text-primary/80"
            >
              <span className="truncate">{t("capabilities.executions.viewConversation")}</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </Card>
  )
}
